package io.horion.cv.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.api.datasource.payement.PaymentMethod
import io.horion.cv.components.Footer
import io.horion.cv.components.LoadingPopup
import io.horion.cv.components.PaymentFailedPopup
import io.horion.cv.components.Topbar
import io.horion.cv.di.koinGet
import io.horion.cv.pages.navigation.Screen
import io.horion.cv.styles.*
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.viewModel.PayViewModel
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
@Page("/confirm-payment")
fun ConfirmPaymentPage() {

    val ctx = rememberPageContext()
    val payViewModel = koinGet<PayViewModel>()
    val payUiState = payViewModel.paymentUiState
    val isOrange = payUiState.method == PaymentMethod.OM

    LaunchedEffect(payUiState.paymentSuccessLancer){
        if (payUiState.paymentSuccessLancer) {
            ctx.router.navigateTo(Screen.PaymentProcessing.path)
        }
    }

    LaunchedEffect(Unit){
        if (payUiState.phone.isEmpty()){
            ctx.router.navigateTo(Screen.Payment.path)
        }
    }

    if (payUiState.error != null) {
        PaymentFailedPopup(
            message = payUiState.error,
            onDismiss = {
                payViewModel.dismissError()
                ctx.router.navigateTo(Screen.Payment.path)
            }
        )
    }

    LoadingPopup(payUiState.loading)

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val pageBg     = if (isDark) Colors.DarkBG else Colors.White
    val titleColor = if (isDark) Colors.White   else Colors.DarkBlue
    val iconFilter = if (isDark) "none"         else "invert(0.8)"

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(pageBg)
            .styleModifier { property("transition", "background-color 300ms ease") },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Topbar()

        Column(
            modifier = ConfirmPageContainerStyle.toModifier(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Row(
                modifier = ConfirmHeaderRowStyle.toModifier().fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {

                FaChevronLeft(
                    modifier = ConfirmBackIconStyle.toModifier()
                        .margin(right = 15.px)
                        .cursor(Cursor.Pointer)
                        .styleModifier { property("filter", iconFilter) }
                        .onClick { ctx.router.navigateTo(Screen.Payment.path) }
                )

                SpanText(
                    text = "Confirmer votre paiement",
                    modifier = ConfirmTitleStyle.toModifier()
                        .color(titleColor)
                        .fontWeight(FontWeight.Bold)
                        .fontFamily(INTER)
                )
            }


            Div(
                attrs = ConfirmContentWrapperStyle.toModifier().toAttrs()
            ) {
                ConfirmFormSection(
                    isOrange = isOrange,
                    phone = payUiState.phone,
                    onInitPayment = { payViewModel.initPayment() }
                )
                PriceSummaryCard()
            }

        }

        Footer()
    }
}

@Composable
fun ConfirmFormSection(
    isOrange: Boolean,
    phone: String,
    modifier: Modifier = Modifier,
    onInitPayment: () -> Unit
) {

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    val labelColor  = if (isDark) Colors.White      else Colors.DarkBlue
    val cardBg      = if (isDark) Colors.holder     else Colors.fillbox
    val phoneBg     = if (isDark) Colors.holder     else Colors.PriceGray
    val phoneColor  = if (isDark) Colors.White      else Colors.Primary

    Column(
        modifier = ConfirmFormSectionStyle.toModifier().then(modifier)
    ) {
        SpanText(
            text = "Opérateur",
            modifier = ConfirmFormLabelStyle.toModifier()
                .fontFamily(INTER)
                .fontWeight(FontWeight.Medium)
                .color(labelColor)
                .margin(bottom = 12.px)
        )

        Column(
            modifier = ConfirmOperatorCardStyle.toModifier()
                .borderRadius(20.px)
                .backgroundColor(cardBg)
                .styleModifier { property("transition", "background-color 300ms ease") }
                .border(
                    width = 1.px,
                    style = LineStyle.Solid,
                    color = if (isOrange) Colors.orange else Colors.yellow
                ),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Image(
                src = if (isOrange) "/orange.png" else "/mtn.png",
                description = if (isOrange) "Orange Money" else "MTN Mobile Money",
                modifier = (if (isOrange) ConfirmOrangeImageStyle else ConfirmMtnImageStyle)
                    .toModifier()
                    .objectFit(ObjectFit.Contain)
                    .styleModifier {
                        property("filter", if (isDark) "brightness(1.1)" else "none")
                    }
            )
        }

        SpanText(
            text = "Numéro",
            modifier = ConfirmFormLabelStyle.toModifier()
                .fontFamily(INTER)
                .fontWeight(FontWeight.Medium)
                .color(labelColor)
                .margin(bottom = 10.px)
        )

        SpanText(
            text = phone.ifEmpty { "—" },
            modifier = ConfirmPhoneDisplayTextStyle.toModifier()
                .fillMaxWidth()
                .fontFamily(INTER)
                .color(phoneColor)
                .backgroundColor(phoneBg)
                .styleModifier { property("transition", "background-color 300ms ease") }
                .borderRadius(10.px)
                .padding(12.px)
        )

        Button(
            onClick = { onInitPayment() },
            modifier = SubscribeButtonStyle.toModifier()
                .then(ConfirmButtonStyle.toModifier())
                .fontFamily(INTER)
                .fontWeight(FontWeight.Medium)
                .borderRadius(30.px)
                .color(Colors.White)
        ) {
            Text("Lancer le paiement")
        }
    }
}
